<template>
  <v-row class="evaluator-rooms-view">
    <v-col>
      <v-expansion-panels class="roomsList" v-model="roomPanelOpen" tile>
        <v-expansion-panel
          class="overflow-y-scroll pt-0 mt-0"
          v-for="room in selectedFloorRooms"
          :key="room.room_id"
        >
          <v-expansion-panel-header
            class="pr-0 panel-header"
            disable-icon-rotate
            :expand-icon="
              roomPanelOpen === selectedFloorRooms.indexOf(room)
                ? '$iconChevronDown'
                : '$iconChevronLeft'
            "
          >
            <v-row class="d-flex justify-space-between pr-8 align-baseline"
              ><span class="subtitle-1">{{ room.name }}</span>
              <span
                :class="
                  testsEvaluated(room) === totalTests(room) ? 'text-green' : ''
                "
                style="font-size: 10px"
              >
                {{
                  $t("evaluation.summary", [
                    testsEvaluated(room),
                    totalTests(room),
                  ])
                }}
              </span>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pa-0 ma-0">
            <v-list-item-group>
              <div
                v-for="(test, index) of room.illumination_tests.concat(
                  room.functional_tests
                )"
                :key="test.illumination_test_id || test.functional_test_id"
              >
                <v-divider :class="index === 0 ? '' : 'ml-6'"></v-divider>
                <v-list-item
                  @click="
                    selectTest(
                      room,
                      test.illumination_test_id || test.functional_test_id
                    )
                  "
                  outlined
                  two-line
                  class="ml-6 pr-2"
                >
                  <v-list-item-content>
                    <v-list-item-title class="mb-3">
                      <v-row
                        class="
                          d-flex
                          justify-space-between
                          align-baseline
                          flex-nowrap
                          mr-0
                          pr-0
                          ml-0
                        "
                      >
                        <span class="text-truncate">{{ test.name }}</span>
                        <span
                          class="overline pl-1"
                          :class="
                            test.is_compliant !== null
                              ? 'text-green'
                              : 'text-grey'
                          "
                        >
                          {{
                            test.is_compliant !== null
                              ? $t("evaluation.evaluated")
                              : $t("evaluation.unevaluated")
                          }}
                        </span>
                      </v-row>
                    </v-list-item-title>
                    <v-list-item-subtitle
                      class="evaluator-rooms-view__requirements"
                    >
                      {{
                        !!test.test_requirements
                          ? `${test.test_requirements}`
                          : " - "
                      }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </div>
            </v-list-item-group>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>
    <guidance-button page="evaluation.rooms_list" />
  </v-row>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import { eventBus } from "@/components/EventBus";
import GuidanceButton from "@/components/guidance/GuidanceButton";

export default {
  name: "evaluator-rooms-view",
  components: { GuidanceButton },
  data: () => ({
    roomPanelOpen: 0,
  }),
  computed: {
    ...mapState("projectStore", ["buildings"]),
    ...mapState("floorStore", ["floor_id"]),
    ...mapState("buildingStore", ["building_id"]),
    ...mapState("roomStore", ["room_types"]),
    selectedFloorRooms() {
      if (!this.building_id || !this.floor_id) {
        return [];
      }

      const buildingIndex = this.buildings.findIndex(
        (building) => building.building_id === this.building_id
      );
      const floorIndex = this.buildings[buildingIndex].floors.findIndex(
        (floor) => floor.floor_id === this.floor_id
      );

      return this.buildings[buildingIndex].floors[floorIndex].rooms || [];
    },
  },
  methods: {
    ...mapMutations("roomStore", [
      "setRoomId",
      "setRoomName",
      "setRoomUse",
      "setRoomInfoComment",
      "setRoomLength",
      "setMeasurementHeight",
      "setMeasurementPointsComment",
      "setIsRoomClosed",
      "setRoomCorners",
      "setDistanceBetweenPointsX",
      "setDistanceBetweenPointsY",
      "setSelectedTestId",
      "setFreezeDistanceBetweenPoints",
      "setMeasurementPoints",
      "setIlluminationTests",
      "setFunctionalTests",
    ]),
    nrOfFunctionalTestsEvaluated(room) {
      if (!room.functional_tests) {
        return 0;
      }

      return room.functional_tests.filter((test) => test.is_compliant !== null)
        .length;
    },
    nrOfIlluminationTestsEvaluated(room) {
      if (!room.illumination_tests) {
        return 0;
      }

      return room.illumination_tests.filter(
        (test) => test.is_compliant !== null
      ).length;
    },
    testsEvaluated(room) {
      return (
        this.nrOfFunctionalTestsEvaluated(room) +
        this.nrOfIlluminationTestsEvaluated(room)
      );
    },
    totalTests(room) {
      return room.illumination_tests.length + room.functional_tests.length;
    },
    selectTest(room, test_id) {
      this.setSelectedTestId(test_id);
      this.setRoomId(room.room_id);
      this.setRoomName(room.name);
      this.setRoomUse(room.room_type);
      this.setRoomInfoComment(room.room_info_comment);
      this.setMeasurementPointsComment(room.measurement_points_comment);
      this.setRoomLength(room.longer_size_length ?? 0);
      this.setMeasurementHeight(room.measurement_height);
      this.setDistanceBetweenPointsX(room.distance_between_points_x);
      this.setDistanceBetweenPointsY(room.distance_between_points_y);

      setTimeout(() => {
        this.setFreezeDistanceBetweenPoints(true);
        this.setIsRoomClosed(room.corners.length > 0);
      }, 1);

      const corners = room.corners.map((corner) => {
        return {
          ...corner,
        };
      });
      this.setRoomCorners(corners ?? []);

      const measurement_points = room.measurement_points.map((point) => {
        return {
          ...point,
        };
      });
      setTimeout(() => {
        this.setMeasurementPoints(measurement_points ?? []);
      }, 1);

      const illumination_tests = room.illumination_tests.map((test) => {
        return {
          ...test,
        };
      });

      this.setIlluminationTests(illumination_tests);
      const functional_tests = room.functional_tests.map((test) => {
        return {
          ...test,
        };
      });

      this.setFunctionalTests(functional_tests);
      eventBus.$emit("centerRoom");
    },
  },
  watch: {
    selectedFloorRooms() {
      setTimeout(() => {
        this.roomPanelOpen = 0;
      }, 1);
    },
  },
};
</script>

<style lang="scss">
.evaluator-rooms-view {
  &__requirements {
    overflow: initial;
    white-space: initial;
  }

  .v-expansion-panel-header {
    min-height: 40px !important;
    max-height: 40px !important;
  }
  .theme--dark.v-list-item--active:hover::before {
    opacity: 0.08 !important;
  }
  .theme--dark.v-list-item--active::before {
    opacity: 0 !important;
  }
}
</style>
